/* eslint-disable no-alert */
import { Search } from '@mui/icons-material';
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Stack } from '@mui/system';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LoadingIconButton from 'src/components/LoadingIconButton';
import OnOff from 'src/components/OnOff';
import PageCard from 'src/components/PageCard';
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';
import CachedOutlinedIcon from '@mui/icons-material/CachedOutlined';
import { useNavigate, useParams } from 'react-router-dom';
import type { ExportPresetsValidationIssue } from 'src/backend';
import {
  useArchivePresetMutation,
  useDeletePresetMutation,
  useExportPresetsCsvMutation,
  useGetPresetsSummaryQuery,
  useLazySinglePresetQuery,
  useListPresetsQuery,
  usePresetSyncStatusQuery,
  useSyncPresetsMutation,
  useUnarchivePresetMutation,
  useValidateExportPresetsMutation,
} from 'src/backend';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import PlayCircleOutlinedIcon from '@mui/icons-material/PlayCircleOutlined';
import { MACHINE_GROUP_DETAILS } from 'src/router/constants';
import { generatePath } from 'src/utils';
import ModalDialog from 'src/components/ModalDialog/ModalDialog';
import UploadPresetsForm from './UploadPresetsForm';
import { ROUTE_MACHINES } from './constants';
import { SyncStatus, useMachineGroupContext } from './MachineGroupContext';
import UpsertPresetForm, { PresetFullModel } from './UpsertPresetForm';
import PresetsInfo from './PresetsInfo';

export type Preset = {
  id: string;
  name: string;
  description: string;
  version: number;
  barCodes: string[];
  syncStatus: boolean | null;
  archived: boolean;
};

export type PresetsSummary = {
  total: number;
  archived: number;
  unarchived: number;
};

type Params = {
  id: string;
};

declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides {
    onSearch: (searchText: string | undefined) => void;
    onFilterOnline: (event: React.ChangeEvent<HTMLInputElement>) => void;
  }
}

function CustomToolbar(props: {
  onSearch: (searchText: string | undefined) => void;
}) {
  const { onSearch } = props;
  const [searchText, setSearchText] = useState('');
  return (
    <Stack direction="row-reverse">
      <IconButton
        onClick={() => onSearch(searchText === '' ? undefined : searchText)}
      >
        <Search />
      </IconButton>
      <TextField
        name="search"
        label="Search"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        onKeyUp={(e) => {
          if (e.key === 'Enter') {
            onSearch(searchText === '' ? undefined : searchText);
          }
        }}
      />
    </Stack>
  );
}

function MachineGroupPresets() {
  const { id } = useParams<Params>();
  const navigate = useNavigate();
  const { machineType, customerId, syncStatus } = useMachineGroupContext();
  const [isOngoingSync, setIsOngoingSync] = useState(false);
  const [refetchRequired, setRefetchRequired] = useState(false);
  const archivePreset = useArchivePresetMutation({});
  const unarchivePreset = useUnarchivePresetMutation({});
  const deletePreset = useDeletePresetMutation({});
  const presetSyncStatus = usePresetSyncStatusQuery(id);
  const presetsSummary = useGetPresetsSummaryQuery(id);
  const [editingPresetId, setEditingPresetId] = useState<string | null>(null);
  const [showUpsertPresetForm, setShowUpsertPresetForm] = useState(false);
  const [singlePreset, setSinglePreset] = useState<PresetFullModel | null>(
    null,
  );
  const getSinglePreset = useLazySinglePresetQuery({});
  const exportPresetsCsv = useExportPresetsCsvMutation();
  const validateExportPresets = useValidateExportPresetsMutation();
  const syncPresets = useSyncPresetsMutation();
  const [selectedPresetIds, setSelectedPresetIds] =
    useState<GridRowSelectionModel>([]);

  const archive = useCallback(
    (presetId: string) => {
      archivePreset[0]({ groupId: id, presetIds: [presetId] })
        .unwrap()
        .then(() => {
          setRefetchRequired(true);
        });
    },
    [archivePreset, id],
  );

  useEffect(() => {
    const syncInProgress =
      syncStatus === SyncStatus.Pending || syncStatus === SyncStatus.InProgress;

    setIsOngoingSync(syncInProgress);
  }, [syncStatus]);

  useEffect(() => {
    setRefetchRequired(true);
  }, []);

  const unarchive = useCallback(
    (presetId: string) => {
      unarchivePreset[0]({ groupId: id, presetIds: [presetId] })
        .unwrap()
        .then(() => {
          setRefetchRequired(true);
        });
    },
    [unarchivePreset, id],
  );

  const deletePermanently = useCallback(
    (presetId: string) => {
      deletePreset[0]({ groupId: id, presetId })
        .unwrap()
        .then(() => {
          setRefetchRequired(true);
        });
    },
    [deletePreset, id],
  );

  const fetchAndEditPreset = useCallback(
    (presetId: string) => {
      getSinglePreset[0]({ groupId: id ?? '', presetId })
        .unwrap()
        .then((data) => {
          setEditingPresetId(presetId);
          setSinglePreset(data.data);
          setShowUpsertPresetForm(true);
        });
    },
    [getSinglePreset, id],
  );

  const handleAddSinglePreset = useCallback(() => {
    setSinglePreset(null);
    setEditingPresetId(null);
    setShowUpsertPresetForm(true);
  }, [setSinglePreset, setEditingPresetId, setShowUpsertPresetForm]);

  const requestSync = useCallback(
    (presetIds: string[]) => {
      const startPresetSyncRequest = {
        machineIds: null,
        presetIds,
        includeOffline: false,
      };
      syncPresets[0]({ id, startPresetSyncRequest })
        .unwrap()
        .then(() => {
          const machineGroupMachinesPath = `${generatePath(
            MACHINE_GROUP_DETAILS,
            {
              id,
            },
          )}/${ROUTE_MACHINES}`;
          navigate(machineGroupMachinesPath);
        });
    },
    [id, syncPresets, navigate],
  );

  const archiveSelected = useCallback(
    (presetIds: string[]) => {
      archivePreset[0]({ groupId: id, presetIds })
        .unwrap()
        .then(() => {
          setRefetchRequired(true);
        });
    },
    [archivePreset, id],
  );

  const unarchiveSelected = useCallback(
    (presetIds: string[]) => {
      unarchivePreset[0]({ groupId: id, presetIds })
        .unwrap()
        .then(() => {
          setRefetchRequired(true);
        });
    },
    [id, unarchivePreset],
  );

  const isPresetOutOfSync = useCallback(
    (presetId: string) => !(presetSyncStatus?.data?.data[presetId] ?? false),
    [presetSyncStatus?.data?.data],
  );

  const columns: GridColDef<Preset>[] = [
    {
      field: 'id',
      headerName: 'ID',
      flex: 1,
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
    },
    {
      field: 'barCodes',
      headerName: 'BarCodes',
      sortable: false,
      renderCell: (cellValues) => {
        const barCodesText = cellValues.row.barCodes.join(', ') || '';
        return (
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              cursor: 'pointer',
            }}
            title={cellValues.row.barCodes.join('\n')}
          >
            {barCodesText}
          </Typography>
        );
      },
      flex: 1,
    },
    {
      field: 'description',
      headerName: 'Description',
      valueFormatter: (v) => v.value ?? '-',
      flex: 1,
    },
    {
      field: 'version',
      headerName: 'Version',
    },
    {
      field: 'archived',
      headerName: 'Archive status',
      renderCell: (p) => (
        <OnOff
          value={p.value === false}
          onText="Active"
          offText="Archived"
          unknownText="Unknown"
          width="10rem"
        />
      ),
      flex: 1,
    },
    {
      field: 'syncStatus',
      headerName: 'Sync Status',
      sortable: false,
      renderCell: (p) => {
        const { id } = p.row;
        const isInSync = presetSyncStatus?.data?.data[id] ?? false;
        const text = isInSync ? 'In Sync' : 'Out of Sync';
        const color = isInSync ? 'success' : 'warning';
        return (
          <Chip
            label={<Typography variant="caption">{text}</Typography>}
            sx={{ width: '10rem' }}
            color={color}
          />
        );
      },
      flex: 1,
    },
    {
      field: 'action',
      headerName: 'Actions',
      sortable: false,
      flex: 1.4,
      cellClassName: 'actions-cell',
      renderCell: (params) => {
        const idStr = params.row.id;
        const isOutOfSync = isPresetOutOfSync(idStr);
        return (
          <>
            {isOutOfSync && (
              <LoadingIconButton
                onClick={() => requestSync([idStr])}
                loading={syncPresets[1].isLoading}
                disabled={syncPresets[1].isLoading}
                sx={{ mr: 1 }}
              >
                <Tooltip title="Sync">
                  <PlayCircleOutlinedIcon />
                </Tooltip>
              </LoadingIconButton>
            )}

            <LoadingIconButton
              onClick={() => fetchAndEditPreset(idStr)}
              loading={getSinglePreset[1].isFetching}
              disabled={isOngoingSync}
              sx={{ mr: 1 }}
            >
              <Tooltip title="Edit">
                <EditIcon />
              </Tooltip>
            </LoadingIconButton>

            {params.row.archived ? (
              <>
                <LoadingIconButton
                  onClick={() => unarchive(idStr)}
                  loading={unarchivePreset[1].isLoading}
                  sx={{ color: '#142A4D' }}
                  disabled={isOngoingSync}
                >
                  <Tooltip title="Unarchive">
                    <UnarchiveOutlinedIcon />
                  </Tooltip>
                </LoadingIconButton>
                <LoadingIconButton
                  onClick={() => deletePermanently(idStr)}
                  loading={deletePreset[1].isLoading}
                  sx={{ color: '#142A4D' }}
                  disabled={isOngoingSync}
                >
                  <Tooltip title="Delete permanently">
                    <DeleteOutlineOutlinedIcon />
                  </Tooltip>
                </LoadingIconButton>
              </>
            ) : (
              <LoadingIconButton
                onClick={() => archive(idStr)}
                loading={archivePreset[1].isLoading}
                disabled={isOngoingSync}
              >
                <Tooltip title="Archive">
                  <ArchiveOutlinedIcon />
                </Tooltip>
              </LoadingIconButton>
            )}
          </>
        );
      },
    },
  ];

  const mapPageToCursor = useRef<Map<number, string | undefined>>(
    new Map<number, string>(),
  );
  const filter = useRef<string | undefined>(undefined);

  const [pageState, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const [rowCountState, setRowCountState] = useState({
    rowCount: 10,
    totalRowCount: -1,
    topPage: -1,
  });

  const { data, isLoading, isFetching, refetch } = useListPresetsQuery({
    machineGroupId: id,
    filter: filter.current,
    page: pageState.page,
    take: pageState.pageSize,
  });

  const selectedPresetIdsAsStrings = useMemo(
    () => selectedPresetIds.map((presetId) => presetId.toString()),
    [selectedPresetIds],
  );
  const [selectedPresetArchiveStateById, setSelectedPresetArchiveStateById] =
    useState<Record<string, boolean>>({});

  useEffect(() => {
    setSelectedPresetArchiveStateById((currentState) => {
      const selectedIdsSet = new Set(selectedPresetIdsAsStrings);
      const nextState: Record<string, boolean> = {};

      selectedIdsSet.forEach((selectedId) => {
        if (selectedId in currentState) {
          nextState[selectedId] = currentState[selectedId];
        }
      });

      (data?.data ?? []).forEach((preset) => {
        if (selectedIdsSet.has(preset.id)) {
          nextState[preset.id] = preset.archived;
        }
      });

      const currentStateKeys = Object.keys(currentState);
      const nextStateKeys = Object.keys(nextState);
      const isUnchanged =
        currentStateKeys.length === nextStateKeys.length &&
        nextStateKeys.every(
          (selectedId) => currentState[selectedId] === nextState[selectedId],
        );

      return isUnchanged ? currentState : nextState;
    });
  }, [data?.data, selectedPresetIdsAsStrings]);

  const hasSelectedPresets = selectedPresetIdsAsStrings.length > 0;
  const selectedPresets = useMemo(
    () =>
      selectedPresetIdsAsStrings
        .map((presetId) => ({
          id: presetId,
          archived: selectedPresetArchiveStateById[presetId],
        }))
        .filter(
          (
            preset,
          ): preset is {
            id: string;
            archived: boolean;
          } => preset.archived !== undefined,
        ),
    [selectedPresetArchiveStateById, selectedPresetIdsAsStrings],
  );
  const hasArchiveStateForEverySelectedPreset =
    selectedPresets.length === selectedPresetIdsAsStrings.length;
  const hasSelectedArchivedPresets = selectedPresets.some(
    (preset) => preset.archived,
  );
  const hasSelectedUnarchivedPresets = selectedPresets.some(
    (preset) => !preset.archived,
  );
  const hasMixedArchiveSelection =
    hasSelectedArchivedPresets && hasSelectedUnarchivedPresets;
  const canArchiveSelected =
    hasArchiveStateForEverySelectedPreset &&
    hasSelectedUnarchivedPresets &&
    !hasSelectedArchivedPresets;
  const canUnarchiveSelected =
    hasArchiveStateForEverySelectedPreset &&
    hasSelectedArchivedPresets &&
    !hasSelectedUnarchivedPresets;
  const archiveActionLabel = canUnarchiveSelected
    ? 'Unarchive Selected'
    : 'Archive Selected';
  const archiveActionIcon = canUnarchiveSelected ? (
    <UnarchiveOutlinedIcon />
  ) : (
    <ArchiveOutlinedIcon />
  );
  const archiveActionDisabled =
    archivePreset[1].isLoading ||
    unarchivePreset[1].isLoading ||
    isOngoingSync ||
    !hasArchiveStateForEverySelectedPreset ||
    hasMixedArchiveSelection ||
    (!canArchiveSelected && !canUnarchiveSelected);
  const archiveActionTooltip = useMemo(() => {
    if (!hasArchiveStateForEverySelectedPreset) {
      return 'Archive state for selected presets is still loading.';
    }

    if (hasMixedArchiveSelection) {
      return 'Only archived or unarchived presets can be processed at one time.';
    }

    return '';
  }, [hasArchiveStateForEverySelectedPreset, hasMixedArchiveSelection]);

  const processSelectedArchiveAction = useCallback(() => {
    if (!hasArchiveStateForEverySelectedPreset) {
      return;
    }

    if (canArchiveSelected) {
      archiveSelected(selectedPresetIdsAsStrings);
      return;
    }

    if (canUnarchiveSelected) {
      unarchiveSelected(selectedPresetIdsAsStrings);
    }
  }, [
    archiveSelected,
    canArchiveSelected,
    canUnarchiveSelected,
    hasArchiveStateForEverySelectedPreset,
    selectedPresetIdsAsStrings,
    unarchiveSelected,
  ]);

  const selectedOutOfSyncPresetIds = selectedPresetIds
    .map((presetId) => presetId.toString())
    .filter((presetId) => isPresetOutOfSync(presetId));
  const hasSelectedOutOfSyncPresets = selectedOutOfSyncPresetIds.length > 0;

  useEffect(() => {
    if (refetchRequired) {
      setRefetchRequired(false);
      presetSyncStatus.refetch();
      presetsSummary.refetch();
      refetch();
    }
  }, [refetchRequired, refetch, presetSyncStatus, presetsSummary]);

  useEffect(() => {
    if (!isLoading && !isFetching && pageState.page >= rowCountState.topPage) {
      const previousPagesCount = pageState.page * pageState.pageSize;
      const currentPageCount = data?.data.length ?? 0;
      const nextPage = data?.data.length === pageState.pageSize ? 1 : 0;
      const rowCount = previousPagesCount + currentPageCount + nextPage;
      let totalRowCount = presetsSummary.data?.data?.total ?? -1;
      if (currentPageCount < pageState.pageSize) {
        totalRowCount = previousPagesCount + currentPageCount;
      }

      setRowCountState({
        rowCount,
        totalRowCount: totalRowCount ?? -1,
        topPage: pageState.page,
      });
    }
  }, [
    rowCountState.topPage,
    isLoading,
    isFetching,
    pageState.page,
    pageState.pageSize,
    data?.data.length,
    presetsSummary.data?.data?.total,
  ]);

  const onPaginationModelChange = (m: GridPaginationModel) => {
    if (m.page !== pageState.page) {
      setPaginationModel({
        page: m.page,
        pageSize: pageState.pageSize,
      });
    }
  };

  const resetPaging = () => {
    mapPageToCursor.current = new Map<number, string | undefined>();
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
    setRowCountState({
      rowCount: 10,
      totalRowCount: -1,
      topPage: -1,
    });
  };

  const onSearch = (searchText: string | undefined) => {
    if (searchText !== filter.current) {
      filter.current = searchText;
      resetPaging();
    }
  };

  const [isPopupOpen, openPopup] = useState(false);
  const [isExportNameDialogOpen, setIsExportNameDialogOpen] = useState(false);
  const [exportFileName, setExportFileName] = useState('presets');
  const [skipOptionalProperties, setSkipOptionalProperties] = useState(false);
  const [exportValidationIssues, setExportValidationIssues] = useState<
    ExportPresetsValidationIssue[]
  >([]);

  const handleExportPresets = useCallback(() => {
    setSkipOptionalProperties(false);
    setExportValidationIssues([]);
    setIsExportNameDialogOpen(true);
  }, []);

  const exportPresets = useCallback(
    (skipOptional: boolean) => {
      const name = exportFileName.trim() || 'presets';
      const fileNameWithExtension = name.endsWith('.csv')
        ? name
        : `${name}.csv`;
      const selectedPresetIdsForExport = hasSelectedPresets
        ? selectedPresetIdsAsStrings
        : undefined;

      exportPresetsCsv[0]({
        id: id ?? '',
        presetIds: selectedPresetIdsForExport,
        skipOptionalProperties: skipOptional,
      })
        .unwrap()
        .then(({ blob }) => {
          const url = window.URL.createObjectURL(blob);
          const anchor = document.createElement('a');

          anchor.href = url;
          anchor.download = fileNameWithExtension;
          document.body.appendChild(anchor);
          anchor.click();
          anchor.remove();
          window.URL.revokeObjectURL(url);
          setIsExportNameDialogOpen(false);
          setSkipOptionalProperties(false);
          setExportValidationIssues([]);
        });
    },
    [
      exportFileName,
      exportPresetsCsv,
      hasSelectedPresets,
      id,
      selectedPresetIdsAsStrings,
    ],
  );

  const handleConfirmExport = useCallback(() => {
    if (skipOptionalProperties && exportValidationIssues.length === 0) {
      validateExportPresets[0]({
        id: id ?? '',
        presetIds: hasSelectedPresets ? selectedPresetIdsAsStrings : undefined,
      })
        .unwrap()
        .then(({ data }) => {
          if (data.hasIssues) {
            setExportValidationIssues(data.issues);
            return;
          }

          // No issues — respect the user's skip-optional preference
          exportPresets(skipOptionalProperties);
        });
      return;
    }

    // "Export anyway" after issues — optional properties must be included
    exportPresets(
      exportValidationIssues.length > 0 ? false : skipOptionalProperties,
    );
  }, [
    exportPresets,
    exportValidationIssues.length,
    id,
    hasSelectedPresets,
    skipOptionalProperties,
    selectedPresetIdsAsStrings,
    validateExportPresets,
  ]);

  const handleCloseEditForm = useCallback((refetchRequired: boolean) => {
    setShowUpsertPresetForm(false);
    setEditingPresetId(null);
    setRefetchRequired(refetchRequired);
  }, []);

  return (
    <>
      <UploadPresetsForm
        isOpen={isPopupOpen}
        machineGroupId={id ?? ''}
        machineType={machineType}
        closeForm={() => {
          openPopup(false);
        }}
      />
      <ModalDialog
        open={isExportNameDialogOpen}
        title="Export presets"
        confirmBtnLoading={
          exportPresetsCsv[1].isLoading || validateExportPresets[1].isLoading
        }
        sx={{ '& .MuiDialog-paper': { width: '35em' } }}
        onClose={() => {
          setIsExportNameDialogOpen(false);
          setSkipOptionalProperties(false);
          setExportValidationIssues([]);
        }}
        onCancel={() => {
          setIsExportNameDialogOpen(false);
          setSkipOptionalProperties(false);
          setExportValidationIssues([]);
        }}
        onConfirm={handleConfirmExport}
        confirmBtnText={
          exportValidationIssues.length > 0 ? 'Export anyway' : 'Export'
        }
      >
        <Stack spacing={1} sx={{ mt: 1 }}>
          <TextField
            autoFocus
            fullWidth
            label="File name"
            value={exportFileName}
            onChange={(e) => setExportFileName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleConfirmExport();
            }}
            InputProps={{
              endAdornment: (
                <Typography variant="body2" color="text.secondary">
                  .csv
                </Typography>
              ),
            }}
          />
          <Stack spacing={0.5}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={skipOptionalProperties}
                  onChange={(e) => {
                    setSkipOptionalProperties(e.target.checked);
                    setExportValidationIssues([]);
                  }}
                />
              }
              label="Skip optional properties *"
            />
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', marginLeft: '.5rem' }}
            >
              * If any preset has custom values in optional properties, all
              optional fields will be included
            </Typography>
          </Stack>
          {exportValidationIssues.length > 0 && (
            <Alert severity="warning">
              <Typography variant="body2" sx={{ mb: 1 }}>
                Optional properties cannot be skipped for this export. Review
                detected non-default values and export anyway to include them.
              </Typography>
              <Stack
                spacing={0.5}
                sx={{ maxHeight: '12rem', overflowY: 'auto' }}
              >
                {exportValidationIssues.map((issue) => {
                  const presetLabel =
                    issue.presetPartNumber || issue.presetName;
                  const stepLabel = issue.stepOrder
                    ? ` (Step ${issue.stepOrder})`
                    : '';
                  const issueKey = `${issue.presetPartNumber}-${
                    issue.presetName
                  }-${issue.propertyName}-${issue.stepOrder ?? 'preset'}-${
                    issue.value
                  }`;
                  return (
                    <Typography
                      key={issueKey}
                      variant="caption"
                      component="div"
                    >
                      {presetLabel}: {issue.propertyName}
                      {stepLabel} = {issue.value}
                    </Typography>
                  );
                })}
              </Stack>
            </Alert>
          )}
        </Stack>
      </ModalDialog>
      {showUpsertPresetForm && (
        <UpsertPresetForm
          machineGroupId={id ?? ''}
          customerId={customerId}
          machineType={machineType}
          presetId={editingPresetId}
          preset={singlePreset}
          closeForm={handleCloseEditForm}
        />
      )}
      <PageCard
        title="Presets"
        icon={<TableRowsOutlinedIcon />}
        sx={{
          '& .MuiCardHeader-root': {
            height: '80px',
          },
        }}
        contentSx={{ display: 'block' }}
        action={
          <>
            {hasSelectedPresets && (
              <Tooltip
                title={archiveActionTooltip}
                disableHoverListener={!hasMixedArchiveSelection}
              >
                <span>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={archiveActionIcon}
                    onClick={processSelectedArchiveAction}
                    disabled={archiveActionDisabled}
                    sx={{ mr: 1 }}
                  >
                    {archiveActionLabel}
                  </Button>
                </span>
              </Tooltip>
            )}
            <Button
              variant="contained"
              startIcon={<PlayCircleOutlinedIcon />}
              onClick={() => requestSync(selectedOutOfSyncPresetIds)}
              disabled={
                syncPresets[1].isLoading || !hasSelectedOutOfSyncPresets
              }
              sx={{ mr: 1 }}
            >
              Sync Selected
            </Button>
            <IconButton onClick={() => handleAddSinglePreset()}>
              <Tooltip title="Add new preset">
                <AddCircleOutlineOutlinedIcon />
              </Tooltip>
            </IconButton>
            <IconButton
              onClick={() => openPopup(true)}
              disabled={isOngoingSync}
            >
              <Tooltip title="Bulk insert presets">
                <FileUploadOutlinedIcon fontSize="large" />
              </Tooltip>
            </IconButton>
            <LoadingIconButton
              loading={
                exportPresetsCsv[1].isLoading ||
                validateExportPresets[1].isLoading
              }
              onClick={handleExportPresets}
              disabled={isExportNameDialogOpen}
            >
              <Tooltip
                title={
                  hasSelectedPresets
                    ? 'Export selected presets to CSV'
                    : 'Export presets to CSV'
                }
              >
                <FileDownloadOutlinedIcon fontSize="large" />
              </Tooltip>
            </LoadingIconButton>
            <LoadingIconButton
              loading={isLoading || isFetching}
              onClick={() => setRefetchRequired(true)}
            >
              <Tooltip title="Refresh">
                <CachedOutlinedIcon fontSize="large" />
              </Tooltip>
            </LoadingIconButton>
          </>
        }
      >
        <PresetsInfo presetsSummary={presetsSummary.data?.data} />
        <DataGrid
          classes={{ row: 'selectable' }}
          columns={columns}
          getRowClassName={(params) => {
            if (params.row.archived) return 'archived';
            return '';
          }}
          rows={data?.data ?? []}
          initialState={{
            columns: {
              columnVisibilityModel: {
                id: false,
              },
            },
          }}
          loading={isFetching}
          slots={{ toolbar: CustomToolbar }}
          slotProps={{
            toolbar: {
              onSearch,
            },
            pagination: { count: rowCountState.totalRowCount },
          }}
          sx={{
            '& .archived': {
              backgroundColor: 'rgba(236, 240, 241, 0.5)',
              color: 'lightgrey',
            },
            '& .archived .actions-cell': {
              color: 'rgba(0, 0, 0, 0.87)',
            },
            '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
              display: 'none',
            },
            '& .MuiDataGrid-overlay': {
              height: '100%',
            },
            '.MuiDataGrid-columnHeader:focus-within, .MuiDataGrid-cell:focus-within, .MuiDataGrid-columnHeader:focus':
              {
                outline: 'none',
              },
          }}
          rowCount={rowCountState.rowCount}
          paginationMode="server"
          onPaginationModelChange={onPaginationModelChange}
          rowSelectionModel={selectedPresetIds}
          onRowSelectionModelChange={(newSelectionModel) =>
            setSelectedPresetIds(newSelectionModel)
          }
          paginationModel={pageState}
          pageSizeOptions={[10]}
          checkboxSelection
          keepNonExistentRowsSelected
          rowHeight={65}
        />
      </PageCard>
    </>
  );
}

export default MachineGroupPresets;
